import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import limits from '../../data/ca_income_rent_limits.json';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// Bedroom types map to the rent-array index in ca_income_rent_limits.json
// [Studio, 1BR, 2BR, 3BR, 4BR, 5BR]
const BEDROOMS = [
    { i: 0, label: 'Studio' },
    { i: 1, label: '1 Bed' },
    { i: 2, label: '2 Bed' },
    { i: 3, label: '3 Bed' },
    { i: 4, label: '4 Bed' },
];

const COUNTIES = Object.entries(limits)
    .map(([slug, d]) => ({ slug, name: d.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

const CREDIT_TYPES = [
    { value: '9', label: '9% (competitive)' },
    { value: '4', label: '4% (bond)' },
];

// Local brand tokens + scoped styles — modernized native <select> and a
// joined-toggle "segmented" control for the short credit-type choice.
const FPF_BLUE = '#0044EB', FPF_INK = '#16203C', FPF_SLATE = '#54648A', FPF_LINE = '#D7E2F1';

const fpfCss = `
.fpf__select{appearance:none;-webkit-appearance:none;-moz-appearance:none;width:100%;cursor:pointer;
  font-family:'Axiforma Regular',sans-serif;font-size:15px;color:${FPF_INK};background-color:#F7FAFD;
  border:1.5px solid ${FPF_LINE};border-radius:12px;padding:12px 40px 12px 14px;
  background-image:url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2354648A' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 14px center;background-size:16px;
  transition:border-color .15s,box-shadow .15s,background-color .15s;}
.fpf__select:hover{border-color:#B9CBEA}
.fpf__select:focus{outline:none;border-color:${FPF_BLUE};background-color:#fff;box-shadow:0 0 0 4px rgba(0,68,235,.10)}
.fpf__seg{display:inline-flex;flex-wrap:wrap;background:#F1F5FB;border:1.5px solid ${FPF_LINE};border-radius:13px;padding:4px;gap:3px}
.fpf__segbtn{border:none;background:transparent;border-radius:9px;padding:9px 17px;font-family:'Axiforma SemiBold',sans-serif;font-size:13.5px;color:${FPF_SLATE};cursor:pointer;transition:.15s;white-space:nowrap}
.fpf__segbtn:hover:not([aria-pressed=true]){color:${FPF_INK}}
.fpf__segbtn[aria-pressed=true]{background:#fff;color:${FPF_BLUE};box-shadow:0 3px 10px rgba(6,16,54,.12)}
`;

// Joined toggle bar for short (2-4 option) single-select fields, e.g. credit type.
const Segmented = ({ options, value, onChange, ariaLabel }) => (
    <div className="fpf__seg" role="group" aria-label={ariaLabel}>
        {options.map((o) => (
            <button type="button" key={o.value} className="fpf__segbtn" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
                {o.label}
            </button>
        ))}
    </div>
);

const num = (v, min, max) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
};
const money = (n) => '$' + Math.round(n).toLocaleString('en-US');
const money1 = (n) => '$' + (Math.round(n / 1000) * 1000).toLocaleString('en-US');

const Field = ({ label, suffix, prefix, ...rest }) => (
    <div className="mb-3">
        <label className="input_title d-block mb-1" style={{ fontSize: '.82rem' }}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {prefix && <span style={{ color: '#6c757d' }}>{prefix}</span>}
            <input className="form-control" type="number" {...rest} />
            {suffix && <span style={{ color: '#6c757d', fontSize: '.85rem' }}>{suffix}</span>}
        </div>
    </div>
);

const FeasibilityProFormaPage = () => {
    usePageSeo({
        title: 'LIHTC Feasibility Pro Forma Calculator — Acquisition Go/No-Go',
        description: 'Free LIHTC acquisition pro forma. Estimate total development cost, 9%/4% equity, supportable debt, and the funding gap — with county rent limits built in.',
        keywords: 'affordable housing pro forma, LIHTC feasibility calculator, acquisition feasibility, development pro forma, funding gap, supportable debt, tax credit equity, 9% 4% LIHTC California, BUILDLAB',
    });

    const [county, setCounty] = useState('los-angeles');
    const [ami, setAmi] = useState('60');
    const [mix, setMix] = useState({ 0: 0, 1: 20, 2: 20, 3: 10, 4: 0 });

    const [acq, setAcq] = useState(5000000);
    const [hardPerUnit, setHardPerUnit] = useState(300000);
    const [softPct, setSoftPct] = useState(25);
    const [devFeePct, setDevFeePct] = useState(15);

    const [creditType, setCreditType] = useState('9');
    const [eligPct, setEligPct] = useState(90);
    const [boost, setBoost] = useState(false);
    const [creditPrice, setCreditPrice] = useState(0.9);

    const [ua, setUa] = useState(50);
    const [vacancy, setVacancy] = useState(5);
    const [opexUnit, setOpexUnit] = useState(8000);
    const [reserveUnit, setReserveUnit] = useState(350);
    const [rate, setRate] = useState(6);
    const [amort, setAmort] = useState(30);
    const [dscr, setDscr] = useState(1.15);

    const amiLevels = useMemo(
        () => Object.keys(limits[county].rent || {}).sort((a, b) => Number(b) - Number(a)),
        [county]
    );

    const r = useMemo(() => {
        const rentRow = (limits[county].rent || {})[ami] || [];
        const units = BEDROOMS.reduce((s, b) => s + (mix[b.i] || 0), 0);

        // Gross potential rent (annual), net of utility allowance
        let gprMonthly = 0;
        BEDROOMS.forEach((b) => {
            const gross = rentRow[b.i] || 0;
            const net = Math.max(0, gross - ua);
            gprMonthly += (mix[b.i] || 0) * net;
        });
        const gpr = gprMonthly * 12;
        const egi = gpr * (1 - vacancy / 100);
        const opexAnnual = units * opexUnit + units * reserveUnit;
        const noi = egi - opexAnnual;

        // Supportable permanent loan (sized on NOI / DSCR)
        const annualDS = noi > 0 ? noi / (dscr || 1) : 0;
        const mr = rate / 100 / 12;
        const n = amort * 12;
        const loan = annualDS <= 0 ? 0 : (mr > 0
            ? (annualDS / 12) * (1 - Math.pow(1 + mr, -n)) / mr
            : (annualDS / 12) * n);

        // Total development cost
        const hard = hardPerUnit * units;
        const soft = (softPct / 100) * hard;
        const devFee = (devFeePct / 100) * (acq + hard + soft);
        const tdc = acq + hard + soft + devFee;

        // LIHTC equity (assumes 100% affordable / applicable fraction = 1.0)
        const eligibleBasis = (hard + soft + devFee) * (eligPct / 100) * (boost ? 1.3 : 1);
        const creditRate = creditType === '9' ? 0.09 : 0.04;
        const annualCredit = eligibleBasis * creditRate;
        const equity = annualCredit * 10 * creditPrice;

        const gap = tdc - loan - equity;
        const tdcPerUnit = units > 0 ? tdc / units : 0;

        // Basic 15-year cash flow (rents +2%/yr, expenses +3%/yr, level debt service)
        const flow = [];
        for (let y = 1; y <= 15; y++) {
            const egiY = egi * Math.pow(1.02, y - 1);
            const opexY = opexAnnual * Math.pow(1.03, y - 1);
            const noiY = egiY - opexY;
            flow.push({ y, noi: noiY, ds: annualDS, cf: noiY - annualDS });
        }

        return { units, gpr, egi, noi, loan, hard, soft, devFee, tdc, equity, gap, tdcPerUnit, flow, annualCredit };
    }, [county, ami, mix, acq, hardPerUnit, softPct, devFeePct, creditType, eligPct, boost, creditPrice, ua, vacancy, opexUnit, reserveUnit, rate, amort, dscr]);

    const setMixVal = (i, v) => setMix((m) => ({ ...m, [i]: num(v, 0, 2000) }));
    const gapPositive = r.gap > 0;
    const showYears = r.flow.filter((f) => [1, 5, 10, 15].includes(f.y));

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Affordable Housing Feasibility Pro Forma'} pagesub={'Pro Forma'} pageTop={'Affordable Housing'} />
                <JsonLd data={webAppSchema({ name: 'LIHTC Feasibility Pro Forma Calculator', description: 'Free LIHTC acquisition pro forma — total development cost, 9%/4% equity, supportable debt, and the funding gap.', path: '/feasibility-pro-forma' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <style dangerouslySetInnerHTML={{ __html: fpfCss }} />
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Should you pursue this site? Find out before you tie it up.</h2>
                                <p>
                                    A fast acquisition-stage pro forma for California LIHTC deals. Enter the unit mix, AMI target,
                                    asking price, and a few cost assumptions — we pull the <strong>restricted rents for your county</strong>
                                    {' '}automatically and return total development cost, tax-credit equity, supportable debt, and the{' '}
                                    <strong>funding gap</strong> you'd need to fill. It's the napkin math, online — not underwriting.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Inputs */}
                            <div className="col-lg-7">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '22px 26px', marginBottom: 18 }}>
                                    <h3 style={{ fontSize: '1.02rem' }} className="mb-3">Project &amp; units</h3>
                                    <div className="row">
                                        <div className="col-sm-6">
                                            <label className="input_title d-block mb-1" style={{ fontSize: '.82rem' }}>County (sets rents)</label>
                                            <select className="fpf__select mb-3" value={county} onChange={(e) => setCounty(e.target.value)} aria-label="County">
                                                {COUNTIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-sm-6">
                                            <label className="input_title d-block mb-1" style={{ fontSize: '.82rem' }}>Target AMI</label>
                                            <select className="fpf__select mb-3" value={ami} onChange={(e) => setAmi(e.target.value)} aria-label="AMI level">
                                                {amiLevels.map((a) => <option key={a} value={a}>{a}% AMI</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <label className="input_title d-block mb-2" style={{ fontSize: '.82rem' }}>Units by bedroom</label>
                                    <div className="row">
                                        {BEDROOMS.map((b) => (
                                            <div className="col" key={b.i}>
                                                <label style={{ fontSize: '.78rem', color: '#6c757d' }}>{b.label}</label>
                                                <input className="form-control" type="number" min="0" max="2000" value={mix[b.i]} onChange={(e) => setMixVal(b.i, e.target.value)} aria-label={`${b.label} units`} />
                                            </div>
                                        ))}
                                    </div>
                                    <p className="small text-muted mb-0 mt-2">{r.units} total units.</p>
                                </div>

                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '22px 26px', marginBottom: 18 }}>
                                    <h3 style={{ fontSize: '1.02rem' }} className="mb-3">Costs</h3>
                                    <div className="row">
                                        <div className="col-sm-6"><Field label="Acquisition price" prefix="$" min="0" max="500000000" step="50000" value={acq} onChange={(e) => setAcq(num(e.target.value, 0, 500000000))} /></div>
                                        <div className="col-sm-6"><Field label="Hard cost / unit" prefix="$" min="0" max="2000000" step="5000" value={hardPerUnit} onChange={(e) => setHardPerUnit(num(e.target.value, 0, 2000000))} /></div>
                                        <div className="col-sm-6"><Field label="Soft costs (% of hard)" suffix="%" min="0" max="100" value={softPct} onChange={(e) => setSoftPct(num(e.target.value, 0, 100))} /></div>
                                        <div className="col-sm-6"><Field label="Developer fee (% of cost)" suffix="%" min="0" max="30" value={devFeePct} onChange={(e) => setDevFeePct(num(e.target.value, 0, 30))} /></div>
                                    </div>
                                </div>

                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '22px 26px', marginBottom: 18 }}>
                                    <h3 style={{ fontSize: '1.02rem' }} className="mb-3">Tax-credit equity</h3>
                                    <div className="row">
                                        <div className="col-sm-6">
                                            <label className="input_title d-block mb-1" style={{ fontSize: '.82rem' }}>Credit type</label>
                                            <div className="mb-3">
                                                <Segmented options={CREDIT_TYPES} value={creditType} onChange={setCreditType} ariaLabel="Credit type" />
                                            </div>
                                        </div>
                                        <div className="col-sm-6"><Field label="Credit price ($/credit)" prefix="$" min="0" max="1.2" step="0.01" value={creditPrice} onChange={(e) => setCreditPrice(num(e.target.value, 0, 1.2))} /></div>
                                        <div className="col-sm-6"><Field label="Eligible basis (% of cost)" suffix="%" min="0" max="100" value={eligPct} onChange={(e) => setEligPct(num(e.target.value, 0, 100))} /></div>
                                        <div className="col-sm-6 d-flex align-items-center">
                                            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: '.86rem' }}>
                                                <input type="checkbox" checked={boost} onChange={() => setBoost((v) => !v)} />
                                                DDA / QCT 130% basis boost
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '22px 26px' }}>
                                    <h3 style={{ fontSize: '1.02rem' }} className="mb-3">Operating &amp; debt</h3>
                                    <div className="row">
                                        <div className="col-sm-4"><Field label="Utility allow./unit/mo" prefix="$" min="0" max="1000" value={ua} onChange={(e) => setUa(num(e.target.value, 0, 1000))} /></div>
                                        <div className="col-sm-4"><Field label="Vacancy" suffix="%" min="0" max="30" value={vacancy} onChange={(e) => setVacancy(num(e.target.value, 0, 30))} /></div>
                                        <div className="col-sm-4"><Field label="OpEx / unit / yr" prefix="$" min="0" max="50000" step="250" value={opexUnit} onChange={(e) => setOpexUnit(num(e.target.value, 0, 50000))} /></div>
                                        <div className="col-sm-4"><Field label="Reserve / unit / yr" prefix="$" min="0" max="5000" step="50" value={reserveUnit} onChange={(e) => setReserveUnit(num(e.target.value, 0, 5000))} /></div>
                                        <div className="col-sm-4"><Field label="Interest rate" suffix="%" min="0" max="15" step="0.1" value={rate} onChange={(e) => setRate(num(e.target.value, 0, 15))} /></div>
                                        <div className="col-sm-4"><Field label="Amortization (yrs)" min="5" max="40" value={amort} onChange={(e) => setAmort(num(e.target.value, 5, 40))} /></div>
                                        <div className="col-sm-4"><Field label="Min DSCR" min="1" max="2" step="0.05" value={dscr} onChange={(e) => setDscr(num(e.target.value, 1, 2))} /></div>
                                    </div>
                                </div>
                            </div>

                            {/* Output */}
                            <div className="col-lg-5">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', position: 'sticky', top: 100 }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>{gapPositive ? 'Estimated funding gap' : 'Estimated surplus'}</div>
                                    <div style={{ fontSize: '2.1rem', fontWeight: 800, lineHeight: 1.1, margin: '2px 0 14px', color: gapPositive ? '#FFC9A3' : '#7CFFB2' }}>
                                        {money1(Math.abs(r.gap))}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 12px', fontSize: '.88rem' }}>
                                        <span style={{ opacity: 0.8 }}>Total development cost</span><strong style={{ textAlign: 'right' }}>{money1(r.tdc)}</strong>
                                        <span style={{ opacity: 0.8 }}>TDC / unit</span><strong style={{ textAlign: 'right' }}>{money(r.tdcPerUnit)}</strong>
                                        <span style={{ opacity: 0.8 }}>LIHTC equity ({creditType}%)</span><strong style={{ textAlign: 'right' }}>{money1(r.equity)}</strong>
                                        <span style={{ opacity: 0.8 }}>Supportable loan</span><strong style={{ textAlign: 'right' }}>{money1(r.loan)}</strong>
                                        <span style={{ opacity: 0.8 }}>Year-1 NOI</span><strong style={{ textAlign: 'right' }}>{money(r.noi)}</strong>
                                    </div>

                                    <div style={{ borderTop: '1px solid rgba(255,255,255,.15)', marginTop: 14, paddingTop: 12 }}>
                                        <div style={{ fontSize: '.82rem', opacity: 0.8, marginBottom: 6 }}>Cash flow after debt service</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: '.85rem' }}>
                                            {showYears.map((f) => (
                                                <React.Fragment key={f.y}>
                                                    <span style={{ opacity: 0.8 }}>Year {f.y}</span>
                                                    <strong style={{ textAlign: 'right', color: f.cf >= 0 ? '#7CFFB2' : '#FF8A8A' }}>{money(f.cf)}</strong>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>

                                    {gapPositive ? (
                                        <p style={{ fontSize: '.82rem', opacity: 0.8, marginTop: 12 }}>
                                            The gap is filled with soft money — HCD/AHSC, HOME, city/county gap loans, and deferred
                                            developer fee. A large gap isn't a no; it's your soft-funding target.
                                        </p>
                                    ) : (
                                        <p style={{ fontSize: '.82rem', opacity: 0.8, marginTop: 12 }}>
                                            Sources exceed costs at these assumptions — sanity-check rents, credit price, and basis.
                                        </p>
                                    )}
                                    <Link to="/contact" className="btn btn-primary w-100 mt-2" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Pressure-test this deal">Pressure-test this deal</span>
                                    </Link>
                                    <div style={{ fontSize: '.8rem', opacity: 0.8, marginTop: 12 }}>
                                        Related: <Link to="/income-rent-limits" style={{ color: '#FFC9A3' }}>Rent limits</Link> ·{' '}
                                        <Link to="/tcac-scoring-estimator" style={{ color: '#FFC9A3' }}>9% scoring</Link>
                                    </div>
                                    <ToolLeadCapture
                                        tool="Feasibility Pro Forma"
                                        summary={`${r.units} units · TDC ${money1(r.tdc)} (${money(r.tdcPerUnit)}/unit) · LIHTC equity ${money1(r.equity)} (${creditType}%) · Supportable loan ${money1(r.loan)} · Funding gap ${money1(r.gap)}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            Planning-level feasibility estimate only — not underwriting, an appraisal, or a credit-pricing commitment.
                            Restricted rents are pulled from the 2025 CTCAC limits (gross, before the project utility allowance);
                            the model assumes a 100% affordable applicable fraction and a fixed 9% / 4% credit rate. Eligible basis,
                            basis boost, credit price, and soft-cost ratios are simplifying assumptions you should confirm with your
                            tax-credit consultant and lender. BUILDLAB designs the technology scope (broadband, WiFi, security,
                            accessibility) inside these budgets.
                        </p>

                        <p className="small mt-3" style={{ maxWidth: 880 }}>
                            A browser-based alternative to an Excel pro forma template — no spreadsheet to download or maintain.
                            When the deal pencils, stress-test it with the{' '}
                            <Link to="/proforma-pressure-test">Pro Forma Pressure Test</Link>.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default FeasibilityProFormaPage;
